import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Shield, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * Two-Factor Authentication (TOTP) management card for staff + patients.
 *
 * Props:
 *  - audience: "patient" | "staff" (default "staff"). Switches the copy so
 *    patients see language about protecting their own health info, not about
 *    admin access.
 *
 * States:
 *  - MFA disabled: shows "Enable Two-Factor Authentication" button → opens
 *    enrollment modal with QR code + verification input + backup codes reveal.
 *  - MFA enabled:  shows "Enabled" badge + "Regenerate backup codes" +
 *    "Disable MFA" (both require current password).
 *
 * Backup codes are shown exactly once after enrollment / regeneration.
 */
export default function MFACard({ audience = "staff" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { secret, otpauth_uri, qr_data_url }
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/profile/mfa/status");
      setStatus(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEnroll = async () => {
    try {
      const { data } = await API.post("/profile/mfa/enroll");
      setEnrollData(data);
      setShowEnroll(true);
      setVerifyCode("");
      setBackupCodes(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const verifyEnroll = async () => {
    if (!/^\d{6}$/.test(verifyCode)) { toast.error("Enter the 6-digit code"); return; }
    setVerifying(true);
    try {
      const { data } = await API.post("/profile/mfa/verify-enroll", { code: verifyCode });
      setBackupCodes(data.backup_codes);
      toast.success("Two-Factor Authentication enabled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  const disable = async () => {
    if (!disablePassword) { toast.error("Password required"); return; }
    setDisabling(true);
    try {
      await API.post("/profile/mfa/disable", { password: disablePassword });
      toast.success("Two-Factor Authentication disabled");
      setShowDisable(false); setDisablePassword(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDisabling(false); }
  };

  const regenerate = async () => {
    try {
      const { data } = await API.post("/profile/mfa/regenerate-backup-codes", { password: regenPassword });
      setBackupCodes(data.backup_codes);
      toast.success("New backup codes generated");
      setShowRegen(false); setRegenPassword(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeEnroll = () => {
    setShowEnroll(false);
    setEnrollData(null);
    setBackupCodes(null);
    setVerifyCode("");
  };

  const copyCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Backup codes copied to clipboard");
  };

  if (loading) return null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6" data-testid="mfa-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${status?.enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 dark:bg-slate-800 text-slate-400"}`}>
            {status?.enabled ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Two-Factor Authentication
              {status?.enabled && <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 align-middle" data-testid="mfa-enabled-badge">Enabled</span>}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {status?.enabled
                ? `Protected with an authenticator app. ${status.backup_codes_remaining ?? 0} backup codes remaining.`
                : audience === "patient"
                  ? "Add a second sign-in step from your phone to help keep your prescriptions and health info safe if your password is ever stolen."
                  : "Require a second code from your phone when signing in. Strongly recommended for staff and anyone with admin access."
              }
            </p>
          </div>
        </div>
        {status?.enabled ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setShowRegen(true)} data-testid="regen-backup-btn">Regenerate codes</Button>
            <Button variant="outline" size="sm" className="rounded-lg text-xs text-red-600 border-red-200" onClick={() => setShowDisable(true)} data-testid="disable-mfa-btn">Disable</Button>
          </div>
        ) : (
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex-shrink-0" onClick={startEnroll} data-testid="enable-mfa-btn">Enable</Button>
        )}
      </div>

      {/* Enrollment modal */}
      <Dialog open={showEnroll} onOpenChange={closeEnroll}>
        <DialogContent className="max-w-md" data-testid="mfa-enroll-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              {backupCodes ? "Save your backup codes" : "Enable Two-Factor Authentication"}
            </DialogTitle>
          </DialogHeader>

          {!backupCodes ? (
            <div className="space-y-4">
              <ol className="text-xs text-slate-600 dark:text-slate-300 list-decimal pl-4 space-y-1">
                <li>Install an authenticator app (<b>Google Authenticator</b>, <b>1Password</b>, <b>Authy</b>).</li>
                <li>Scan this QR code (or type the secret manually).</li>
                <li>Enter the 6-digit code it shows to confirm.</li>
              </ol>
              {enrollData?.qr_data_url && (
                <div className="flex justify-center">
                  <img src={enrollData.qr_data_url} alt="Scan with your authenticator app" className="w-44 h-44 rounded-xl border border-slate-200 dark:border-slate-800" data-testid="mfa-qr-code" />
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">Or enter this secret</p>
                <div className="font-mono text-xs bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 rounded-lg p-2 text-slate-700 dark:text-slate-200 break-all" data-testid="mfa-secret">
                  {enrollData?.secret}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">6-digit code</Label>
                <Input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="mt-1 font-mono text-center tracking-[0.4em] rounded-xl"
                  data-testid="mfa-verify-input"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeEnroll}>Cancel</Button>
                <Button onClick={verifyEnroll} disabled={verifying || verifyCode.length !== 6} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="mfa-verify-btn">
                  {verifying ? "Verifying..." : "Enable 2FA"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 dark:text-amber-300">Save these backup codes somewhere safe. Each one can be used once if you lose access to your authenticator app. <b>You won&apos;t see them again.</b></p>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="mfa-backup-codes">
                {backupCodes.map(c => (
                  <div key={c} className="font-mono text-sm text-center bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 rounded-lg py-2 text-slate-800 dark:text-slate-100 select-all">{c}</div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={copyCodes} data-testid="mfa-copy-codes-btn"><Copy className="w-4 h-4 mr-1" /> Copy all</Button>
                <Button onClick={closeEnroll} className="bg-purple-600 hover:bg-purple-700 text-white">I&apos;ve saved them</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable modal */}
      <Dialog open={showDisable} onOpenChange={() => { setShowDisable(false); setDisablePassword(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Disable Two-Factor Authentication</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">Enter your password to confirm. Your backup codes will also be deleted.</p>
            <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} placeholder="Current password" className="rounded-xl" data-testid="mfa-disable-password" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(""); }}>Cancel</Button>
            <Button onClick={disable} disabled={disabling || !disablePassword} className="bg-red-600 hover:bg-red-700 text-white" data-testid="mfa-confirm-disable-btn">
              {disabling ? "Disabling..." : "Disable 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate modal */}
      <Dialog open={showRegen} onOpenChange={() => { setShowRegen(false); setRegenPassword(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Regenerate backup codes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">Your old backup codes will be invalidated and replaced with 10 new ones.</p>
            <Input type="password" value={regenPassword} onChange={(e) => setRegenPassword(e.target.value)} placeholder="Current password" className="rounded-xl" data-testid="mfa-regen-password" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRegen(false); setRegenPassword(""); }}>Cancel</Button>
            <Button onClick={regenerate} disabled={!regenPassword} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="mfa-confirm-regen-btn">Regenerate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
